import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useShopifyManageUrl } from 'src/modules/payment/hooks/use-shopify-billing'
import { useSubscription } from 'src/modules/payment/hooks/use-subscription'
import { isShopifyBilledProfile } from 'src/modules/payment/utils/helpers'
import { invalidateBillingQueries } from 'src/modules/payment/utils/invalidate-billing-queries'
import { waitForPaymentResumed } from 'src/modules/payment/utils/wait-for-subscription'
import { useProfile } from 'src/modules/user/hooks/use-profile'

/** Opens the card-update flow, then refreshes billing state once a Paddle payment pause clears. */
export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient()
  const profile = useProfile()
  const isShopifyBilled = isShopifyBilledProfile(profile.data)
  const subscription = useSubscription({}, { enabled: !!profile.data?.billing_subscription_id || isShopifyBilled })
  const shopifyManageUrl = useShopifyManageUrl({ enabled: isShopifyBilled })
  const [isAwaitingUpdate, setIsAwaitingUpdate] = useState(false)
  const { refetch } = profile
  const updateUrl = subscription.data?.update_url
  const shopifyAdminUrl = shopifyManageUrl.data?.shopify_admin_url

  const handleUpdateComplete = useCallback(async () => {
    setIsAwaitingUpdate(true)
    try {
      await waitForPaymentResumed(refetch)
      invalidateBillingQueries(queryClient)
    } finally {
      // `openUpdateCard` is a no-op while awaiting, so a stuck flag would kill the button until remount.
      setIsAwaitingUpdate(false)
    }
  }, [queryClient, refetch])

  const openUpdateCard = useCallback(() => {
    if (isShopifyBilled) {
      if (shopifyAdminUrl) window.top?.location.assign(shopifyAdminUrl)
      return
    }

    if (!updateUrl || isAwaitingUpdate) return
    window.Paddle.Checkout.open({ override: updateUrl, successCallback: handleUpdateComplete })
  }, [handleUpdateComplete, isAwaitingUpdate, isShopifyBilled, shopifyAdminUrl, updateUrl])

  return { openUpdateCard, isAvailable: isShopifyBilled || !!updateUrl, isAwaitingUpdate }
}
